import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { IconArrowRight, IconCaretDown, Tag } from '@vtex/shoreline'

import Table from '~/components/Table'

import { createOriginsSchema } from './schema'
import type { WalletBalanceOriginProps, WalletBalanceOriginRow } from './types'

import styles from './styles.module.css'

const OriginsTable = ({
  rows,
  remaining
}: {
  rows: WalletBalanceOriginRow[]
  remaining: boolean
}) => {
  const { t } = useTranslation()
  const schema = useMemo(
    () => createOriginsSchema({ remaining, t }),
    [remaining, t]
  )

  return <Table schema={schema} data={rows} total={rows.length} />
}

export const WalletBalanceOrigin = ({
  origins,
  showHeading,
  walletDetailTo
}: WalletBalanceOriginProps) => {
  const { t } = useTranslation()
  const expiredPanelId = useId()
  const [expiredOpen, setExpiredOpen] = useState(false)

  const active = origins.filter(row => !row.expired)
  const expired = origins.filter(row => row.expired)

  if (!active.length && !expired.length) return null

  const table = (
    <>
      {active.length ? <OriginsTable rows={active} remaining={false} /> : null}
      {expired.length ? (
        <div className={styles.expiredWrap}>
          <button
            type="button"
            className={styles.expiredToggle}
            aria-expanded={expiredOpen}
            aria-controls={expiredPanelId}
            onClick={() => setExpiredOpen(open => !open)}
          >
            <span className={styles.expiredToggleLabel}>
              {t('rm:wallet.balanceOrigin.expired.heading')}
              <Tag variant="secondary" color="gray">
                {expired.length}
              </Tag>
            </span>
            <span
              className={
                expiredOpen
                  ? `${styles.expiredCaret} ${styles.expiredCaretOpen}`
                  : styles.expiredCaret
              }
              aria-hidden
            >
              <IconCaretDown />
            </span>
          </button>
          <section
            id={expiredPanelId}
            hidden={!expiredOpen}
            className={styles.expiredPanel}
            aria-label={t('rm:wallet.balanceOrigin.expired.heading')}
          >
            {expiredOpen ? (
              <>
                <OriginsTable rows={expired} remaining />
                {walletDetailTo ? (
                  <Link
                    to={walletDetailTo}
                    className={styles.walletDetailLink}
                    aria-label={t('rm:wallet.list.viewFullHistoryOnWalletPage')}
                  >
                    {t('rm:wallet.list.viewFullHistoryOnWalletPage')}
                    <IconArrowRight />
                  </Link>
                ) : null}
              </>
            ) : null}
          </section>
        </div>
      ) : null}
    </>
  )

  return (
    <section
      className={
        showHeading ? `${styles.root} ${styles.withHeading}` : styles.root
      }
      data-show-heading={showHeading ? 'true' : undefined}
    >
      {showHeading ? (
        <>
          <div className={styles.sectionHead}>
            <h2 className={styles.heading}>
              {t('rm:wallet.balanceOrigin.heading')}
            </h2>
          </div>
          <div className={styles.sectionBody}>{table}</div>
        </>
      ) : (
        table
      )}
    </section>
  )
}
